import React from "react";
import { Card, CardContent } from "../components/ui/Card";

export default function Terms() {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-gradient-to-r from-shuttlebee-dark to-shuttlebee-navy text-white py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold mb-2">Terms of Service</h1>
          <p className="text-white/80">Last Updated: January 10, 2026</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-12">
        <Card>
          <CardContent className="pt-6 prose prose-gray max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Acceptance of Terms</h2>
              <p className="text-gray-700">
                By accessing or using the Shuttlebee SCH-Summerside transportation services ("Services"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use our Services.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Services Provided</h2>
              <p className="text-gray-700 mb-4">
                Shuttlebee provides transportation services for students to and from Summerside Camps at SCH and other designated locations. Our services include:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Daily shuttle transportation to summer camps and schools</li>
                <li>Pre-arranged pickup and drop-off services</li>
                <li>Professional drivers with background checks and certifications</li>
                <li>Small group transportation in safe, inspected vehicles</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. User Accounts</h2>
              <p className="text-gray-700 mb-4">
                To use our Services, you must:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Be at least 18 years old and a parent or legal guardian of the child(ren) using our services</li>
                <li>Provide accurate, complete, and current information</li>
                <li>Maintain the security of your account credentials</li>
                <li>Notify us immediately of any unauthorized use of your account</li>
              </ul>
              <p className="text-gray-700 mt-4">
                You are responsible for all activities that occur under your account.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Quote Requests and Bookings</h2>
              <p className="text-gray-700 mb-4">
                When you submit a quote request:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>The quote is not a confirmed booking until you receive written confirmation from us</li>
                <li>Pricing is subject to availability and may vary based on route, distance, and number of students</li>
                <li>We reserve the right to decline service requests at our discretion</li>
                <li>Multi-student discounts and promotional pricing are subject to terms and availability</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Payment Terms</h2>
              <p className="text-gray-700 mb-4">
                Payment for services is required according to the following terms:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Payment is due prior to the start of services unless otherwise agreed</li>
                <li>Prices are quoted per week per student for the specified service period</li>
                <li>Late payments may result in suspension of services</li>
                <li>Refunds are handled on a case-by-case basis but cannot be honored if they impact the integrity of the route. Seat purchases can be transferred to a student of a similar age.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Cancellation and Refund Policy</h2>
              <p className="text-gray-700 mb-4">
                <strong>Cancellations by You:</strong>
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                <li>Cancellations made before March 1: Full refund</li>
              </ul>
              <p className="text-gray-700 mb-4">
                <strong>Cancellations by Us:</strong>
              </p>
              <p className="text-gray-700">
                We reserve the right to cancel services due to weather, safety concerns, or other circumstances. In such cases, you will receive a full refund for unused services if services are cancelled for more than one day.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Parent/Guardian Responsibilities</h2>
              <p className="text-gray-700 mb-4">
                As a parent or guardian, you agree to:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Ensure your child is ready at the designated pickup location at the scheduled time</li>
                <li>Provide accurate information about your child's needs (car seats, allergies, accessibility)</li>
                <li>Authorize another adult to receive your child if you cannot be present at drop-off</li>
                <li>Notify us immediately of schedule changes or absences</li>
                <li>Ensure your child follows driver instructions and behavioral guidelines</li>
                <li>Keep emergency contact information current</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Safety and Conduct</h2>
              <p className="text-gray-700 mb-4">
                We are committed to providing safe transportation. We expect:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Students to remain seated and use seat belts/car seats as required</li>
                <li>Respectful behavior toward drivers and other passengers</li>
                <li>Compliance with all safety rules and driver instructions</li>
              </ul>
              <p className="text-gray-700 mt-4">
                We reserve the right to suspend or terminate services for students who pose safety risks or engage in disruptive behavior, without refund.
              </p>
              <p className="text-gray-700 mt-4">
                <strong>Vehicle Monitoring and Compliance:</strong> For the safety and security of all passengers, vehicles may be equipped with in-vehicle monitoring systems including cameras and GPS tracking. All transportation services are conducted in accordance with Pennsylvania state laws and regulations governing student transportation.
              </p>
              <p className="text-gray-700 mt-4">
                <strong>Vehicle Damage:</strong> Parents/guardians are responsible for any damage to the vehicle caused by their student. Additional fees may be assessed to cover repair costs if the vehicle is damaged in any way by your student.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Liability and Insurance</h2>
              <p className="text-gray-700 mb-4">
                <strong>Our Liability:</strong>
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>We maintain appropriate commercial auto insurance and liability coverage</li>
                <li>Our liability is limited to direct damages caused by our negligence</li>
                <li>We are not liable for indirect, incidental, or consequential damages</li>
                <li>Maximum liability is limited to the amount paid for services</li>
              </ul>
              <p className="text-gray-700 mt-4">
                <strong>Your Responsibilities:</strong> You acknowledge that transportation involves inherent risks and agree to release Shuttlebee from claims except those arising from our gross negligence or willful misconduct.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Schedule Changes</h2>
              <p className="text-gray-700">
                We will make reasonable efforts to maintain published schedules. However, routes and schedules may change due to traffic, weather, or operational needs. We will notify you of significant changes as soon as possible.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Intellectual Property</h2>
              <p className="text-gray-700">
                All content on our website and in our materials (logos, text, images, software) is owned by Shuttlebee or licensed to us. You may not use our intellectual property without written permission.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Termination</h2>
              <p className="text-gray-700">
                We reserve the right to terminate or suspend access to our Services immediately, without prior notice, for conduct that we believe violates these Terms, is harmful to other users, or is otherwise inappropriate.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Dispute Resolution</h2>
              <p className="text-gray-700 mb-4">
                <strong>Informal Resolution:</strong> If you have a concern, please contact us first at <a href="mailto:hello@getshuttlebee.com" className="text-shuttlebee-dark hover:underline">hello@getshuttlebee.com</a> so we can try to resolve it.
              </p>
              <p className="text-gray-700 mb-4">
                <strong>Governing Law:</strong> These Terms are governed by the laws of the Commonwealth of Pennsylvania.
              </p>
              <p className="text-gray-700">
                <strong>Arbitration:</strong> Any disputes arising from these Terms or our Services will be resolved through binding arbitration in Philadelphia County, Pennsylvania, rather than in court.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">14. Changes to Terms</h2>
              <p className="text-gray-700">
                We may modify these Terms at any time. Material changes will be communicated via email or website notice. Continued use of our Services after changes constitutes acceptance of the modified Terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">15. Contact Information</h2>
              <p className="text-gray-700 mb-4">
                For questions about these Terms, please contact us:
              </p>
              <div className="bg-gray-50 rounded-lg p-4 text-gray-700">
                <p className="font-semibold mb-2">Shuttlebee - SCH Summerside Program</p>
                <p>Email: <a href="mailto:hello@getshuttlebee.com" className="text-shuttlebee-dark hover:underline">hello@getshuttlebee.com</a></p>
                <p>Phone: (484) 324-2559</p>
              </div>
            </section>

            <div className="mt-12 pt-8 border-t border-gray-200">
              <p className="text-sm text-gray-600">
                By using our services, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
